import { StandardEvents } from '@shopify/events';

/** Four links, progressively enhanced with the theme's own search and cart. */
class TavilumeMobileNav extends HTMLElement {
  #controller;
  #cartRevision = 0;
  #keyboardFrame = 0;
  #openedDialog = null;
  #mobile = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    document.addEventListener('theme-drawer:open', this.#syncDialogs, { signal });
    document.addEventListener('theme-drawer:close', this.#syncDialogs, { signal });
    // DialogComponent's custom events do not bubble; capture is intentional.
    document.addEventListener('dialog:open', this.#syncDialogs, { capture: true, signal });
    document.addEventListener('dialog:close', this.#syncDialogs, { capture: true, signal });
    document.addEventListener('close', this.#syncDialogs, { capture: true, signal });
    document.addEventListener('close', this.#onNativeDialogClose, { capture: true, signal });
    document.addEventListener('focusin', this.#queueKeyboardUpdate, { signal });
    document.addEventListener('focusout', this.#queueKeyboardUpdate, { signal });
    this.#mobile.addEventListener('change', this.#queueKeyboardUpdate, { signal });
    window.visualViewport?.addEventListener('resize', this.#queueKeyboardUpdate, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });

    this.#renderCount(Number(this.dataset.cartCount));
    this.#restoreRecentCount();
    this.#syncDialogs();
    this.#queueKeyboardUpdate();
    // These modules may finish after this one. Navigation works as a normal
    // link in the meantime, including if an optional drawer is unavailable.
    for (const name of ['theme-drawer', 'dialog-component']) {
      customElements.whenDefined(name).then(() => {
        if (!signal.aborted) this.#syncDialogs();
      });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#openedDialog = null;
    cancelAnimationFrame(this.#keyboardFrame);
    document.documentElement.removeAttribute('data-tvm-keyboard-open');
  }

  #onClick = (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('[data-tvm-dialog]');
    if (!link || !this.contains(link)) return;

    const isSearch = link.dataset.tvmDialog === 'search';
    const component = document.getElementById(isSearch ? 'search-modal' : 'cart-drawer');
    const method = isSearch ? 'showDialog' : 'open';
    if (typeof component?.[method] !== 'function') return;

    event.preventDefault();
    // Safari does not always focus tapped links. The drawer stores this focus
    // target and returns to it when closed, just like the header trigger.
    link.focus({ preventScroll: true });
    this.#openedDialog = { panel: component.querySelector(':scope > dialog'), link };
    component[method]();
  };

  #onNativeDialogClose = (event) => {
    const opened = this.#openedDialog;
    // DrawerCloseEvent fires before the closing animation; the native close
    // event marks the actual end. Ignore temporary close/reopen transitions.
    if (!opened || event.target !== opened.panel || opened.panel.open) return;
    this.#openedDialog = null;
    if (!this.#mobile.matches || !opened.link.isConnected) return;
    if (document.querySelector('dialog[open], theme-drawer[open], details.menu-open, shopify-chat[open]')) return;

    // An input focused when Escape is pressed can leave the keyboard flag
    // set during the theme's own focus return. Reveal this navigation first,
    // then restore only the link that opened this particular dialog.
    cancelAnimationFrame(this.#keyboardFrame);
    document.documentElement.removeAttribute('data-tvm-keyboard-open');
    opened.link.focus({ preventScroll: true });
  };

  #syncDialogs = () => {
    for (const [name, id] of [['search', 'search-modal'], ['cart', 'cart-drawer']]) {
      const link = this.querySelector(`[data-tvm-dialog="${name}"]`);
      const component = document.getElementById(id);
      const method = name === 'search' ? 'showDialog' : 'open';
      if (!link) continue;
      if (typeof component?.[method] !== 'function') {
        link.removeAttribute('aria-haspopup');
        link.removeAttribute('aria-controls');
        link.removeAttribute('aria-expanded');
        continue;
      }
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', id);
      link.setAttribute('aria-expanded', String(
        name === 'search' ? Boolean(component.querySelector('dialog[open]')) : component.hasAttribute('open')
      ));
    }
  };

  #onCartUpdate = (event) => {
    if (!event.promise) return;
    const revision = ++this.#cartRevision;
    const { signal } = this.#controller;
    event.promise.then(({ cart, detail }) => {
      if (signal.aborted || revision !== this.#cartRevision) return;
      // Same resolved payload used by assets/cart-icon.js; no duplicate
      // add/change requests, cart storage, or guessed event names.
      this.#renderCount(cart?.totalQuantity ?? detail?.itemCount);
    }).catch(() => {
      // The theme owns cart error feedback. Keep the last confirmed count.
    });
  };

  #renderCount(count) {
    if (!Number.isInteger(count) || count < 0) return;
    const badge = this.querySelector('[data-tvm-cart-count]');
    const link = this.querySelector('[data-tvm-dialog="cart"]');
    if (!badge || !link) return;
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.hidden = count === 0;
    link.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
    this.dataset.cartCount = String(count);
  }

  #restoreRecentCount() {
    // The header already writes this 10-second navigation cache.
    try {
      const saved = JSON.parse(sessionStorage.getItem('cart-count') || 'null');
      if (saved && Date.now() - saved.timestamp < 10000) this.#renderCount(Number(saved.value));
    } catch (_) {
      // Storage can be unavailable in private browsing.
    }
  }

  #onPageShow = async (event) => {
    this.#queueKeyboardUpdate();
    this.#syncDialogs();
    if (!event.persisted) return;
    const revision = ++this.#cartRevision;
    const { signal } = this.#controller;
    try {
      const endpoint = `${this.dataset.cartUrl.replace(/\/$/, '')}.js`;
      const response = await fetch(endpoint, { credentials: 'same-origin', cache: 'no-store', signal });
      if (!response.ok) return;
      const cart = await response.json();
      if (!signal.aborted && revision === this.#cartRevision) this.#renderCount(cart.item_count);
    } catch (_) {
      if (!signal.aborted && revision === this.#cartRevision) this.#restoreRecentCount();
    }
  };

  #queueKeyboardUpdate = () => {
    cancelAnimationFrame(this.#keyboardFrame);
    this.#keyboardFrame = requestAnimationFrame(() => {
      let focused = document.activeElement;
      while (focused?.shadowRoot?.activeElement) focused = focused.shadowRoot.activeElement;
      const editable = focused?.matches(
        'textarea, [contenteditable]:not([contenteditable="false"]), input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not([type="color"]):not([type="hidden"])'
      );
      document.documentElement.toggleAttribute('data-tvm-keyboard-open', this.#mobile.matches && Boolean(editable));
    });
  };
}

if (!customElements.get('tavilume-mobile-nav')) {
  customElements.define('tavilume-mobile-nav', TavilumeMobileNav);
}
